import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";

const CATS = [
  ["MATERIAL", "Material"], ["LABOUR", "Labour"], ["SUBCONTRACT", "Subcontract"], ["PLANT", "Equipment"], ["OTHER", "Other"],
];
export const inr0 = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export function WBSCostDialog({ projectId, item, canEdit, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [busy, setBusy] = useState(false);
  const itemQty = Number(item.quantity || 0);

  useEffect(() => {
    api.get(`/projects/${projectId}/wbs-costs`).then(({ data }) => {
      setRows(data.lines.filter((l) => l.wbs_item_id === item.id).map((l) => ({
        id: l.id, category: l.category, description: l.description, rate: String(l.rate),
        quantity: l.quantity_is_custom ? String(l.quantity) : "",
      })));
    }).catch((err) => toast.error(getErrorMessage(err)));
  }, [projectId, item.id]);

  const add = (category) => setRows((r) => [...r, { id: null, category, description: CATS.find((c) => c[0] === category)[1], rate: "", quantity: "" }]);
  const upd = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const del = (i) => {
    const row = rows[i];
    if (row.id) setRemoved((r) => [...r, row.id]);
    setRows((r) => r.filter((_, j) => j !== i));
  };
  const amount = (r) => (r.quantity === "" ? itemQty : Number(r.quantity)) * Number(r.rate || 0);
  const total = rows.reduce((s, r) => s + amount(r), 0);

  const save = async () => {
    setBusy(true);
    try {
      for (const id of removed) await api.delete(`/projects/${projectId}/wbs-cost-lines/${id}`);
      for (const r of rows) {
        if (!r.description.trim()) continue;
        const body = { category: r.category, description: r.description.trim(), uom: item.uom || null,
          quantity: r.quantity === "" ? null : Number(r.quantity), rate: Number(r.rate || 0) };
        if (r.id) await api.patch(`/projects/${projectId}/wbs-cost-lines/${r.id}`, body);
        else await api.post(`/projects/${projectId}/wbs/${item.id}/cost-lines`, body);
      }
      toast.success("Cost lines saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="wbs-cost-dialog">
        <DialogHeader><DialogTitle>Budget · {item.code} {item.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Activity quantity: <b>{itemQty || "not set"} {item.uom || ""}</b>. Each line below costs this activity. Leave a line&apos;s quantity blank to use the activity quantity.
        </p>
        {!itemQty && <p className="text-xs text-amber-600">This activity has no quantity yet, so lines without their own quantity will show ₹0.</p>}
        <div className="space-y-2">
          {rows.length === 0 && <p className="py-3 text-sm text-muted-foreground">No cost lines yet. Add material, labour, subcontract or equipment below.</p>}
          {rows.length > 0 && (
            <div className="grid grid-cols-[130px_1fr_90px_100px_100px_32px] gap-2 text-xs font-medium text-muted-foreground">
              <span>Type</span><span>Description</span><span>Qty</span><span>Rate</span><span className="text-right">Amount</span><span />
            </div>
          )}
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[130px_1fr_90px_100px_100px_32px] items-center gap-2" data-testid={`wbs-cost-row-${i}`}>
              <Select value={r.category} onValueChange={(v) => upd(i, { category: v })} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={r.description} onChange={(e) => upd(i, { description: e.target.value })} disabled={!canEdit} />
              <Input type="number" min="0" placeholder={String(itemQty || "")} value={r.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} disabled={!canEdit} />
              <Input type="number" min="0" placeholder="0" value={r.rate} onChange={(e) => upd(i, { rate: e.target.value })} disabled={!canEdit} />
              <span className="text-right font-mono text-xs">{inr0(amount(r))}</span>
              {canEdit ? <Button size="sm" variant="ghost" onClick={() => del(i)}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button> : <span />}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Add:</span>
            {CATS.map(([k, l]) => <Button key={k} size="sm" variant="outline" onClick={() => add(k)}><Plus className="h-3 w-3" /> {l}</Button>)}
          </div>
        )}
        <div className="flex justify-between border-t border-border pt-3 text-sm">
          <span className="text-muted-foreground">Budget for this activity</span>
          <span className="font-mono font-semibold" data-testid="wbs-cost-total">{inr0(total)}</span>
        </div>
        <DialogFooter>
          {canEdit && <Button disabled={busy} onClick={save} data-testid="wbs-cost-save">Save cost lines</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
